/**
 * Strategiekarten (Board „Preisautomatik · Einstellungen“): Stempel Systemprofil/Aktiv/Ausgewählt,
 * Name, Korridor aus dem Profil, Version mit „gültig seit“ und Datenstand. „Belegung stark/mittel/
 * schwach“ entfällt – dafür gibt es kein Merkmal am Profil.
 */
import type { PricingStrategyProfileResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { formatDate } from '@/shared/lib/format';
import { formatProfilePercent } from './profileValueFormat';

export const STRATEGY_LABEL: Record<PricingStrategyProfileResponse['strategy'], string> = {
  MaximizeOccupancy: 'Maximale Auslastung',
  Balanced: 'Ausgewogen',
  MaximizeRevenue: 'Maximaler Erlös',
};

interface Props {
  profiles: PricingStrategyProfileResponse[];
  selectedProfileVersionId: string | null;
  activeProfileVersionId: string | null;
  onSelect: (versionId: string) => void;
}

export function ProfileChoiceCards(props: Props) {
  return (
    <div
      role="radiogroup"
      aria-label="Strategie"
      className="grid grid-cols-[repeat(auto-fit,minmax(min(200px,100%),1fr))] gap-2.5"
    >
      {props.profiles.map((profile) => {
        const selected = props.selectedProfileVersionId === profile.versionId;
        const active = props.activeProfileVersionId === profile.versionId;
        return (
          <label
            key={profile.versionId}
            className={cn(
              'flex min-h-[120px] cursor-pointer flex-col gap-1.5 rounded-lg border p-3.5',
              'has-[:focus-visible]:outline-forest has-[:focus-visible]:outline has-[:focus-visible]:outline-2',
              selected ? 'border-forest bg-forest-soft/50 border-2' : 'border-hairline bg-card',
            )}
          >
            <input
              type="radio"
              name="strategy-profile"
              value={profile.versionId}
              checked={selected}
              onChange={() => props.onSelect(profile.versionId)}
              className="sr-only"
            />
            <span className="flex flex-wrap gap-1">
              <Stamp>Systemprofil</Stamp>
              {active && <Stamp tone="green">Aktiv</Stamp>}
              {selected && !active && <Stamp tone="amber">Ausgewählt</Stamp>}
            </span>
            <span className="font-serif text-[19px] font-bold">
              {STRATEGY_LABEL[profile.strategy]}
            </span>
            <span className="text-ink-soft text-xs">
              Korridor {formatProfilePercent(profile.softCorridorMinimumPercent)} /{' '}
              {formatProfilePercent(profile.softCorridorMaximumPercent)}
            </span>
            <span className="text-ink-soft font-mono text-[11px]">
              Version {profile.versionNumber} · Gültig seit {formatDate(profile.validFrom)} ·
              Datenstand {formatDate(profile.calibration.snapshotDate)}
            </span>
          </label>
        );
      })}
    </div>
  );
}

function Stamp({ children, tone }: { children: string; tone?: 'green' | 'amber' }) {
  return (
    <span
      className={cn(
        'rounded-pill inline-flex h-5 items-center px-[7px] text-[10px] font-bold',
        tone === 'green' && 'bg-forest text-white',
        tone === 'amber' && 'bg-amber-tint text-amber-ink',
        !tone && 'bg-card-alt text-ink-soft',
      )}
    >
      {children}
    </span>
  );
}
